/**
 * Date-window math for the Sales & Marketing dashboard
 * (`/marketing/sales-marketing`).
 *
 * The existing Marketing Dashboard speaks three trailing windows (7 / 28 /
 * 90 days). This page is the daily operating view, so the presets are
 * shorter and calendar-aware: today, yesterday, last 6 / 14 / 30 / 90
 * days, month-to-date, year-to-date. Comparison is always the immediately
 * prior equivalent window so every tile can render a delta.
 */

import {
  addUtcDays,
  isoDaysAgo,
  type AnalyticsWindow,
} from '@/features/marketing/range';

export type SalesDashRange =
  | 'today'
  | 'yesterday'
  | '6d'
  | '14d'
  | '30d'
  | '90d'
  | 'mtd'
  | 'ytd';

export type SalesChannelFilter = 'all' | 'shopify' | 'amazon';

export const SALES_DASH_RANGE_OPTIONS: Array<{
  id: SalesDashRange;
  label: string;
  shortLabel: string;
}> = [
  { id: 'today', label: 'Today', shortLabel: 'Today' },
  { id: 'yesterday', label: 'Yesterday', shortLabel: 'Yesterday' },
  { id: '6d', label: 'Last 6 days', shortLabel: '6d' },
  { id: '14d', label: 'Last 14 days', shortLabel: '14d' },
  { id: '30d', label: 'Last 30 days', shortLabel: '30d' },
  { id: '90d', label: 'Last 90 days', shortLabel: '90d' },
  { id: 'mtd', label: 'Month to date', shortLabel: 'MTD' },
  { id: 'ytd', label: 'Year to date', shortLabel: 'YTD' },
];

export const SALES_CHANNEL_FILTERS: Array<{
  id: SalesChannelFilter;
  label: string;
}> = [
  { id: 'all', label: 'All channels' },
  { id: 'shopify', label: 'Shopify' },
  { id: 'amazon', label: 'Amazon' },
];

const RANGE_IDS = new Set<string>(SALES_DASH_RANGE_OPTIONS.map((o) => o.id));

export function isSalesDashRange(value: string | null): value is SalesDashRange {
  return !!value && RANGE_IDS.has(value);
}

export function isSalesChannelFilter(
  value: string | null,
): value is SalesChannelFilter {
  return value === 'all' || value === 'shopify' || value === 'amazon';
}

function utcParts(date = new Date()): { year: number; month: number; day: number } {
  return {
    year: date.getUTCFullYear(),
    month: date.getUTCMonth(),
    day: date.getUTCDate(),
  };
}

function isoFromUtc(year: number, monthIndex: number, day: number): string {
  return new Date(Date.UTC(year, monthIndex, day)).toISOString().slice(0, 10);
}

/** Clamp a year/month/day to a real calendar date (Feb 31 → Feb 28/29). */
function clampUtcDate(year: number, monthIndex: number, day: number): string {
  const lastDay = new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate();
  return isoFromUtc(year, monthIndex, Math.min(day, lastDay));
}

/**
 * Inclusive current + previous windows for a preset.
 *
 * Trailing windows (6d / 14d / 30d / 90d) compare to the immediately prior
 * block of the same length. Calendar windows (MTD / YTD) compare to the
 * same dates last month / last year so "how are we tracking vs last
 * August" stays a calendar question rather than a 30-day rolling one.
 */
export function salesDashWindow(range: SalesDashRange): AnalyticsWindow {
  const today = isoDaysAgo(0);
  const { year, month, day } = utcParts();

  switch (range) {
    case 'today': {
      const yesterday = addUtcDays(today, -1);
      return {
        currentStart: today,
        currentEnd: today,
        previousStart: yesterday,
        previousEnd: yesterday,
      };
    }
    case 'yesterday': {
      const yesterday = addUtcDays(today, -1);
      const prior = addUtcDays(today, -2);
      return {
        currentStart: yesterday,
        currentEnd: yesterday,
        previousStart: prior,
        previousEnd: prior,
      };
    }
    case '6d':
    case '14d':
    case '30d':
    case '90d': {
      const length = Number.parseInt(range, 10);
      const currentStart = addUtcDays(today, -(length - 1));
      const previousEnd = addUtcDays(currentStart, -1);
      const previousStart = addUtcDays(previousEnd, -(length - 1));
      return { currentStart, currentEnd: today, previousStart, previousEnd };
    }
    case 'mtd': {
      const currentStart = isoFromUtc(year, month, 1);
      const prevMonthEnd = addUtcDays(currentStart, -1);
      const prev = utcParts(new Date(`${prevMonthEnd}T00:00:00Z`));
      return {
        currentStart,
        currentEnd: today,
        previousStart: isoFromUtc(prev.year, prev.month, 1),
        previousEnd: clampUtcDate(prev.year, prev.month, day),
      };
    }
    case 'ytd': {
      const currentStart = isoFromUtc(year, 0, 1);
      return {
        currentStart,
        currentEnd: today,
        previousStart: isoFromUtc(year - 1, 0, 1),
        previousEnd: clampUtcDate(year - 1, month, day),
      };
    }
  }
}

export function salesDashComparisonLabel(range: SalesDashRange): string {
  switch (range) {
    case 'today':
      return 'vs yesterday';
    case 'yesterday':
      return 'vs prior day';
    case '6d':
      return 'vs prior 6 days';
    case '14d':
      return 'vs prior 14 days';
    case '30d':
      return 'vs prior 30 days';
    case '90d':
      return 'vs prior 90 days';
    case 'mtd':
      return 'vs last month to date';
    case 'ytd':
      return 'vs last year to date';
  }
}

export function salesDashRangeLabel(range: SalesDashRange): string {
  return SALES_DASH_RANGE_OPTIONS.find((o) => o.id === range)?.label ?? range;
}
